import { getWatchDogIcon } from "@/components/hlidaci-pes/watchdog-page/watchDogOverviewUtils";
import { EstateStatusBadge } from "@/components/portfolio/all-estate-overview/PortfolioEstateCard";
import PercentageIndicator from "@/components/portfolio/PercentageIndicator";
import { SinglePortfolioActionButtons } from "@/components/portfolio/single-estate-overview/SinglePortfolioActionButtons";
import { SummaryMetricGrid } from "@/components/ui/SummaryGrid";
import { OverviewSurface } from "@/components/ui/OverviewSurface";
import { useLanguage } from "@/context/LanguageContext";
import { useEstateContext } from "@/context/EstateContext";
import { cn } from "@/lib/utils";
import type { ClientEstate } from "@/types";
import { formatCZK, normalizeAccessories } from "@/utils/MyEstateHelper";
import {
  ChatBubbleLeftEllipsisIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType, SVGProps } from "react";

interface SingleEstateOverviewProps {
  selectedEstate: ClientEstate;
  embedded?: boolean;
}

type DetailItem = {
  value?: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  href?: string;
  label?: string;
};

function DetailPill({
  item,
  showEmpty = false,
}: {
  item: DetailItem;
  showEmpty?: boolean;
}) {
  const value = item.value?.trim();

  if (!value) {
    return showEmpty ? (
      <span className="hidden min-h-6 items-center px-2 text-[10px] text-softiq-primary/25 dark:text-white/25 lg:flex">
        —
      </span>
    ) : null;
  }

  const { Icon, href } = item;
  const content = (
    <>
      <Icon aria-hidden="true" className="size-3 shrink-0" />
      <span className="min-w-0 truncate">
        {item.label ? `${item.label}: ${value}` : value}
      </span>
    </>
  );
  const className = "app-pill-detail";

  return href ? (
    <a
      href={href}
      title={value}
      className={`${className} hover:bg-softiq-secondary-shade-200 dark:hover:bg-white/15`}
    >
      {content}
    </a>
  ) : (
    <span title={value} className={className}>
      {content}
    </span>
  );
}

const SingleEstateOverview = ({
  selectedEstate,
  embedded = false,
}: SingleEstateOverviewProps) => {
  const { language } = useLanguage();
  const copy = {
    cs: {
      noAddress: "Bez adresy",
      estimatedValue: "Odhadovaná hodnota",
      purchaseValue: "Pořizovací hodnota",
      purchasePrice: "Pořizovací cena",
      rentalReturn: "Celkový výnos z pronájmu",
      totalValueIncrease: "Celkové navýšení hodnoty",
      manager: "Správce",
      tenant: "Nájemce",
      email: "E-mail",
      accessories: "Příslušenství",
      note: "Poznámka",
    },
    en: {
      noAddress: "No address",
      estimatedValue: "Estimated value",
      purchaseValue: "Acquisition value",
      purchasePrice: "Purchase price",
      rentalReturn: "Total rental return",
      totalValueIncrease: "Total value increase",
      manager: "Manager",
      tenant: "Tenant",
      email: "Email",
      accessories: "Amenities",
      note: "Note",
    },
  }[language];
  const { analysis, totalProfit } = useEstateContext();
  const EstateIcon = getWatchDogIcon(selectedEstate.estateType);
  const purchasePrice = selectedEstate.purchasePrice ?? 0;
  const currentPrice = analysis.avgPriceSell ?? 0;
  const displayedValue = currentPrice > 0 ? currentPrice : purchasePrice;
  const hasEstimatedValue = currentPrice > 0;
  const accessories = normalizeAccessories(selectedEstate.accessories);
  const showExactAddress =
    selectedEstate.exactAddress &&
    selectedEstate.exactAddress !== selectedEstate.address;
  const metadata = [
    selectedEstate.estateType,
    selectedEstate.flatCategory,
    selectedEstate.usableArea ? `${selectedEstate.usableArea} m²` : null,
    selectedEstate.ownerShip,
    selectedEstate.estateCondition,
  ].filter((value): value is string => Boolean(value));
  const contactRows: Array<{ label: string; items: DetailItem[] }> = [
    {
      label: copy.manager,
      items: [
        { value: selectedEstate.managerName, Icon: UserCircleIcon },
        {
          value: selectedEstate.managerPhone,
          Icon: PhoneIcon,
          href: selectedEstate.managerPhone
            ? `tel:${selectedEstate.managerPhone.replace(/\s/g, "")}`
            : undefined,
        },
        {
          value: selectedEstate.managerEmail,
          Icon: EnvelopeIcon,
          label: copy.email,
          href: selectedEstate.managerEmail
            ? `mailto:${selectedEstate.managerEmail}`
            : undefined,
        },
        {
          value: selectedEstate.managerNote,
          Icon: ChatBubbleLeftEllipsisIcon,
        },
      ],
    },
    {
      label: copy.tenant,
      items: [
        { value: selectedEstate.tenantName, Icon: UserCircleIcon },
        {
          value: selectedEstate.tenantPhone,
          Icon: PhoneIcon,
          href: selectedEstate.tenantPhone
            ? `tel:${selectedEstate.tenantPhone.replace(/\s/g, "")}`
            : undefined,
        },
        {
          value: selectedEstate.tenantEmail,
          Icon: EnvelopeIcon,
          label: copy.email,
          href: selectedEstate.tenantEmail
            ? `mailto:${selectedEstate.tenantEmail}`
            : undefined,
        },
        {
          value: selectedEstate.tenantNote,
          Icon: ChatBubbleLeftEllipsisIcon,
        },
      ],
    },
  ].filter((row) => row.items.some((item) => Boolean(item.value?.trim())));
  const supplementaryDetails: Array<{
    label: string;
    items: DetailItem[];
  }> = [
    {
      label: copy.accessories,
      items: accessories.map((value) => ({ value, Icon: CheckCircleIcon })),
    },
    {
      label: copy.note,
      items: [
        {
          value: selectedEstate.note,
          Icon: ChatBubbleLeftEllipsisIcon,
        },
      ],
    },
  ]
    .map((detail) => ({
      ...detail,
      items: detail.items.filter((item) => Boolean(item.value?.trim())),
    }))
    .filter((detail) => detail.items.length > 0);
  const metrics = [
    {
      label: hasEstimatedValue ? copy.estimatedValue : copy.purchaseValue,
      value:
        displayedValue > 0 ? (
          <span className="flex flex-col items-start gap-1.5">
            <span>{formatCZK(displayedValue)}</span>
            {hasEstimatedValue && purchasePrice > 0 ? (
              <span className="inline-flex items-center gap-2">
                <PercentageIndicator
                  valueNow={currentPrice}
                  valueBefore={purchasePrice}
                  variant="banner"
                  locale={language === "cs" ? "cs-CZ" : "en-US"}
                />
                <span className="text-[10px] font-normal text-softiq-primary/45 dark:text-white/45">
                  {copy.totalValueIncrease}
                </span>
              </span>
            ) : null}
          </span>
        ) : (
          "—"
        ),
    },
    {
      label: copy.purchasePrice,
      value: purchasePrice > 0 ? formatCZK(purchasePrice) : "—",
    },
    {
      label: copy.rentalReturn,
      value: formatCZK(totalProfit),
      tone: totalProfit >= 0 ? ("positive" as const) : ("negative" as const),
    },
  ];

  return (
    <OverviewSurface as="section" frame={embedded ? "topBorder" : "card"}>
      {!embedded ? <SinglePortfolioActionButtons /> : null}

      <div
        className={cn(
          "flex flex-col gap-3 px-5 py-3 sm:flex-row sm:items-center sm:px-6",
          !embedded && "border-t border-softiq-primary/10 dark:border-white/10",
        )}
      >
        <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-softiq-secondary-shade-200 text-softiq-primary dark:bg-white/10 dark:text-softiq-secondary">
          <EstateIcon aria-hidden="true" className="size-6" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-lg font-medium tracking-[-0.025em]">
              {selectedEstate.name}
            </h2>
            <EstateStatusBadge status={selectedEstate.status} />
          </div>
          <p className="app-section-description mt-1 truncate">
            {selectedEstate.address || copy.noAddress}
            {showExactAddress ? ` · ${selectedEstate.exactAddress}` : ""}
          </p>
        </div>

        {metadata.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 sm:max-w-[46%] sm:justify-end">
            {metadata.map((item) => (
              <span key={item} className="app-pill-neutral">
                {item}
              </span>
            ))}
          </div>
        ) : null}

        {embedded ? <SinglePortfolioActionButtons showTitle={false} /> : null}
      </div>

      <SummaryMetricGrid metrics={metrics} layout="portfolio" />

      {contactRows.length > 0 || supplementaryDetails.length > 0 ? (
        <div className="border-t border-softiq-primary/10 bg-white/90 dark:border-white/10 dark:bg-softiq-dark-elevated">
          {contactRows.length > 0 ? (
            <>
              <div className="divide-y divide-softiq-primary/10 dark:divide-white/10 lg:hidden">
                {contactRows.map((row) => (
                  <div key={row.label} className="px-5 py-3 sm:px-6">
                    <p className="app-section-label">{row.label}</p>
                    <div className="mt-2 flex min-w-0 flex-wrap gap-1.5">
                      {row.items.map((item, index) => (
                        <DetailPill key={`${row.label}-${index}`} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden grid-cols-[6.5rem_repeat(4,max-content)] items-center justify-start gap-x-2 gap-y-2 px-6 py-3 lg:grid">
                {contactRows.map((row) => (
                  <div key={row.label} className="contents">
                    <p className="app-section-label">{row.label}</p>
                    {row.items.map((item, index) => (
                      <DetailPill
                        key={`${row.label}-${index}`}
                        item={item}
                        showEmpty
                      />
                    ))}
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {supplementaryDetails.length > 0 ? (
            <div
              className={`grid gap-px bg-softiq-primary/10 dark:bg-white/10 sm:grid-cols-2 ${
                contactRows.length > 0
                  ? "border-t border-softiq-primary/10 dark:border-white/10"
                  : ""
              }`}
            >
              {supplementaryDetails.map((detail) => (
                <div
                  key={detail.label}
                  className="min-w-0 bg-white/90 px-5 py-3 dark:bg-softiq-dark-elevated sm:px-6"
                >
                  <p className="app-section-label">{detail.label}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {detail.items.map((item, index) => (
                      <div
                        key={`${detail.label}-${index}`}
                        className="max-w-full"
                      >
                        <DetailPill item={item} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </OverviewSurface>
  );
};

export default SingleEstateOverview;
